import React, { memo, useEffect, useReducer } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { objects, validators } from 'investira.sdk';
import services from '../services';
import withMessage from '../hoc/withMessage';
import withResponseHandling from '../hoc/withResponseHandling';
import { acCarteiraSelected } from '../store/actions';
import { AVALIACAO_SESSOES, AVALIACAO_SESSOES_QUESTOES } from '../enums';

// Context
const AvaliacoesContext = React.createContext();

const { potencialidade_fiduciaria, conflitos_asg, condicoes_regulamentares } = AVALIACAO_SESSOES;

const initialState = {
  avaliacoes: [],
  avaliacao: '',
  steps: [
    { id: 0, label: 'Tipo de Avaliação', last: false, completed: false },
    { id: 1, label: 'Selecionar Modelo', last: false, completed: false },
    { id: 2, label: 'Selecionar Entidade', last: false, completed: false },
    { id: 3, label: 'Confirmar Informações', last: false, completed: false },
    { id: 4, label: 'Requisitar Respostas', last: true, completed: false }
  ],
  gestor: {},
  adm: {},
  fundo: {},
  email_responsavel: '',
  assinatura_responsavel: true,
  requisitar_respostas: false,
  sessoes: {
    [potencialidade_fiduciaria.id]: {
      ...potencialidade_fiduciaria,
      progression: 100,
      completed: true
    },
    [conflitos_asg.id]: {
      ...conflitos_asg,
      progression: 80,
      completed: 0
    },
    [condicoes_regulamentares.id]: {
      ...condicoes_regulamentares,
      progression: 60,
      completed: 0
    }
  }, // { [id]: {id, label, progression, completed} }
  updating: false,
  activeStepConfig: 0, // 0: Tipo | 1: Modelo | 2: Seleção | 3: Confirmação | 4: Requisição
  activeCategoryId: 'potencialidade_fiduciaria',
  activeQuestionId: 1,
  activeAliasQuestionId: 'A.1',
  selectedTipo: '',
  selectedModelo: ''
};

const getAliasFromQuestion = (pState, pQuestionId) => {
  const xCategoryId = pState.activeCategoryId; // 'potencialidade_fiduciaria', 'conflitos_asg', etc.
  const xSessaoObj = AVALIACAO_SESSOES[xCategoryId]; // { id, label, sessao: 'A' | 'B' | 'C' }
  if (!xSessaoObj) {
    return null;
  }

  const xSessaoId = xSessaoObj.id; // ex: 'potencialidade_fiduciaria'
  const xSessaoQuestoes = AVALIACAO_SESSOES_QUESTOES[xSessaoId];
  if (!xSessaoQuestoes) {
    return null;
  }

  const xIndex = xSessaoQuestoes.questoes.findIndex(
    xQuestao => xQuestao.id === Number(pQuestionId)
  );
  if (xIndex === -1) {
    return null;
  }

  const xNumero = xIndex + 1;
  return `${xSessaoObj.sessao}.${xNumero}`; // ex: 'A.1'
};

// Reducers
const reducers = (state = initialState, action) => {
  if (action.type === 'STATE_RESETED') {
    return {
      ...initialState
    };
  }

  if (action.type === 'ACTIVE_STEP_CONFIG_CHANGED') {
    return {
      ...state,
      activeStepConfig: action.payload
    };
  }

  if (action.type === 'AVALIACOES_CHANGED') {
    return {
      ...state,
      avaliacoes: action.payload
    };
  }

  if (action.type === 'STEPS_CHANGED') {
    return {
      ...state,
      steps: action.payload
    };
  }

  if (action.type === 'STEP_CHANGED') {
    const { id, data } = action.payload || {};

    return {
      ...state,
      steps: state.steps.map(xStep => {
        if (xStep.id === id) {
          return {
            ...xStep,
            ...data
          };
        }

        return xStep;
      })
    };
  }

  if (action.type === 'STEPS_RESETED') {
    return {
      ...state,
      steps: initialState.steps
    };
  }

  if (action.type === 'GESTOR_CHANGED') {
    return {
      ...state,
      gestor: action.payload
    };
  }

  if (action.type === 'ADM_CHANGED') {
    return {
      ...state,
      adm: action.payload
    };
  }

  if (action.type === 'FUNDO_CHANGED') {
    return {
      ...state,
      fundo: action.payload
    };
  }

  if (action.type === 'EMAIL_RESPONSAVEL_CHANGED') {
    return {
      ...state,
      email_responsavel: action.payload
    };
  }

  if (action.type === 'ASSINATURA_RESPONSAVEL_CHANGED') {
    return {
      ...state,
      assinatura_responsavel: action.payload
    };
  }

  if (action.type === 'REQUISITAR_RESPOSTAS_CHANGED') {
    return {
      ...state,
      requisitar_respostas: action.payload
    };
  }

  if (action.type === 'ACTIVE_CATEGORY_ID_CHANGED') {
    return {
      ...state,
      activeCategoryId: action.payload
    };
  }

  if (action.type === 'ACTIVE_QUESTION_ID_CHANGED') {
    const xQuestionId = action.payload;
    return {
      ...state,
      activeQuestionId: xQuestionId,
      activeAliasQuestionId: getAliasFromQuestion(state, xQuestionId)
    };
  }

  if (action.type === 'SELECTED_MODELO_CHANGED') {
    return {
      ...state,
      selectedModelo: action.payload
    };
  }

  if (action.type === 'SELECTED_TIPO_CHANGED') {
    return {
      ...state,
      selectedTipo: action.payload
    };
  }

  return state;
};

// Dispatchs

const acResetState = () => ({
  type: 'STATE_RESETED'
});

const acUpdateAvaliacoes = pArray => ({
  type: 'AVALIACOES_CHANGED',
  payload: pArray
});

const acUpdateActiveStepConfig = pStep => ({
  type: 'ACTIVE_STEP_CONFIG_CHANGED',
  payload: pStep
});

const acChangeStep = (pId, pData = {}) => ({
  type: 'STEP_CHANGED',
  payload: {
    id: pId,
    data: pData
  }
});

const acUpdateGestor = pGestor => ({
  type: 'GESTOR_CHANGED',
  payload: pGestor
});

const acUpdateAdm = pAdm => ({
  type: 'ADM_CHANGED',
  payload: pAdm
});

const acUpdateFundo = pFundo => ({
  type: 'FUNDO_CHANGED',
  payload: pFundo
});

const acResetSteps = () => ({
  type: 'STEPS_RESETED'
});

const acUpdateEmailResponsavel = pEmail => ({
  type: 'EMAIL_RESPONSAVEL_CHANGED',
  payload: pEmail
});

const acUpdateAssinaturaResponsavel = pBool => ({
  type: 'ASSINATURA_RESPONSAVEL_CHANGED',
  payload: pBool
});

const acUpdateRequisitarRespostas = pBool => ({
  type: 'REQUISITAR_RESPOSTAS_CHANGED',
  payload: pBool
});

const acUpdateActiveCategoryId = pCategoryId => ({
  type: 'ACTIVE_CATEGORY_ID_CHANGED',
  payload: pCategoryId
});

const acUpdateActiveQuestionId = pQuestionId => ({
  type: 'ACTIVE_QUESTION_ID_CHANGED',
  payload: pQuestionId
});

const acUpdateSelectedModelo = pModelo => ({
  type: 'SELECTED_MODELO_CHANGED',
  payload: pModelo
});

const acUpdateSelectedTipo = pTipo => ({
  type: 'SELECTED_TIPO_CHANGED',
  payload: pTipo
});

// Custom Hook
export const useAvaliacoesContext = () => React.useContext(AvaliacoesContext);

export const AvaliacoesProvider = memo(props => {
  const location = useLocation();
  const navigate = useNavigate();
  const storeDispatch = useDispatch();

  const [state, dispatch] = useReducer(reducers, initialState);

  const handleResetState = () => {
    dispatch(acResetState());
  };

  const handleBulkAddAvaliacoes = (pPosicoes = []) => {
    dispatch(acUpdateAvaliacoes(pPosicoes));
  };

  const handleChangeActiveStepConfig = pStep => {
    dispatch(acUpdateActiveStepConfig(pStep));
  };

  const handleResetSteps = () => {
    dispatch(acResetSteps());
  };

  const handleChangeStep = (pId = null, pData = {}) => {
    dispatch(acChangeStep(pId, pData));
  };

  const handleChangeGestor = pGestor => {
    dispatch(acUpdateGestor(pGestor));
  };

  const handleChangeAdm = pAdm => {
    dispatch(acUpdateAdm(pAdm));
  };

  const handleChangeFundo = pFundo => {
    dispatch(acUpdateFundo(pFundo));
  };

  const handleChangeEmailResponsavel = pEmail => {
    dispatch(acUpdateEmailResponsavel(pEmail));
  };

  const handleChangeAssinaturaResponsavel = pBool => {
    dispatch(acUpdateAssinaturaResponsavel(pBool));
  };

  const handleChangeRequisitarRespostas = pBool => {
    dispatch(acUpdateRequisitarRespostas(pBool));
  };

  const handleChangeActiveSessaoId = pCategoryId => {
    dispatch(acUpdateActiveCategoryId(pCategoryId));
  };

  const handleChangeActiveQuestionId = pQuestionId => {
    dispatch(acUpdateActiveQuestionId(pQuestionId));
  };

  const handleChangeSelectedModelo = pModelo => {
    dispatch(acUpdateSelectedModelo(pModelo));
  };

  const handleChangeSelectedTipo = pTipo => {
    dispatch(acUpdateSelectedTipo(pTipo));
  };

  const getAvaliacaoId = pAvaliacao => {
    if (!pAvaliacao) {
      return null;
    }

    if (typeof pAvaliacao === 'string' || typeof pAvaliacao === 'number') {
      return pAvaliacao;
    }

    return pAvaliacao.avaliacao_id || pAvaliacao.id || null;
  };

  const handleEditAvaliacao = pAvaliacao => {
    const xAvaliacaoId = getAvaliacaoId(pAvaliacao);

    if (!xAvaliacaoId) {
      return;
    }

    navigate(`/avaliacoes/${xAvaliacaoId}`);
  };

  const handleViewAvaliacao = pAvaliacao => {
    const xAvaliacaoId = getAvaliacaoId(pAvaliacao);

    if (!xAvaliacaoId) {
      return;
    }

    navigate(`/avaliacoes/${xAvaliacaoId}/resultado`);
  };

  useEffect(() => {
    if (validators.isEmail(state.email_responsavel)) {
      handleChangeRequisitarRespostas(true);
    } else {
      handleChangeRequisitarRespostas(false);
    }
  }, [state.email_responsavel]);

  useEffect(() => {
    if (state.activeStepConfig !== 4 || !state.selectedModelo) {
      return;
    }

    const xHasEmail = Boolean(state.email_responsavel);
    const xIsValidEmail = validators.isEmail(state.email_responsavel);

    // Step final concluido quando o email esta vazio (seguir sem requisicao)
    // ou quando informado e valido.
    handleChangeStep(4, { completed: !xHasEmail || xIsValidEmail });
  }, [state.selectedModelo, state.email_responsavel, state.activeStepConfig]);

  return (
    <AvaliacoesContext.Provider
      value={{
        state,
        actions: {
          handleResetState,
          handleBulkAddAvaliacoes,
          handleChangeActiveStepConfig,
          handleChangeStep,
          handleResetSteps,
          handleChangeGestor,
          handleChangeAdm,
          handleChangeFundo,
          handleChangeEmailResponsavel,
          handleChangeAssinaturaResponsavel,
          handleChangeRequisitarRespostas,
          handleChangeActiveSessaoId,
          handleChangeActiveQuestionId,
          handleChangeSelectedModelo,
          handleChangeSelectedTipo,
          handleEditAvaliacao,
          handleViewAvaliacao
        }
      }}>
      {props.children}
    </AvaliacoesContext.Provider>
  );
});

AvaliacoesProvider.displayName = 'AvaliacoesProvider';

export default withMessage(withResponseHandling(AvaliacoesProvider));
